import React from 'react';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { TallyConnectPanel } from '../components/TallyConnectPanel';
import { BookOpen, Zap } from 'lucide-react';

export const AccountingIntegrations = () => {
  const { selectedClient } = useClient();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  if (!selectedClient) {
    return (
      <div className="text-center text-text-secondary py-12" data-testid="acct-no-client">
        Select a client to manage accounting integrations.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-integrations-page">
      {/* Header */}
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">
          Accounting Integrations
        </p>
        <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
          <BookOpen className="h-7 w-7" /> Connect Your Books — {selectedClient.name}
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Pull voucher data from your accounting software so we can run 3-way reconciliation
          (POS ↔ Tally ↔ Bank) and catch discrepancies your auditor would miss.
        </p>
      </div>

      {/* Tally section */}
      <section className="bg-white border border-border rounded-lg p-5 shadow-sm" data-testid="tally-direct-section">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-blue-700 font-bold mb-1">
              Recommended · Direct Connector
            </div>
            <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" /> Tally Prime — automated sync
            </h2>
            <p className="text-xs text-text-secondary mt-1">
              A free Windows utility (~8 MB) installed on the accountant's PC pushes sales
              vouchers to us every night. No public IP, no VPN, no manual exports.
            </p>
          </div>
          <div className="hidden sm:flex flex-col items-end text-xs text-text-muted">
            <span>Coming soon for Zoho Books · QuickBooks · ProfitBooks</span>
          </div>
        </div>
        <TallyConnectPanel client={selectedClient} isAdmin={isAdmin} />
      </section>

      {/* Future tiers callout */}
      <div className="bg-slate-50 border border-slate-200 text-slate-800 rounded-md p-4" data-testid="acct-future-tiers">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
          <div>
            <h3 className="text-sm font-semibold mb-1">Coming next</h3>
            <ul className="text-xs space-y-1 list-disc list-inside text-text-secondary">
              <li><strong>Zoho Books</strong> — cloud-based, direct API. Same set-and-forget UX as Petpooja.</li>
              <li><strong>QuickBooks Online</strong> — OAuth flow, useful for international clients.</li>
              <li><strong>ProfitBooks</strong> — popular alt to Tally for small restaurants.</li>
              <li><strong>Bank feeds</strong> — Plaid-equivalent for India (Account Aggregator framework via Setu / FinBox).</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingIntegrations;
